import { useRef, useEffect, forwardRef } from "react"
import Box from "@mui/material/Box"
// eslint-disable-next-line @guusje4525/mui-import-checker
import { styled } from "@mui/material"
import { SxProps, Theme } from "@mui/material/styles"
import TextFieldVerificationStore from "./TextFieldVerificationStore"
import useStore from "reusable/mobx-store-utils"
import { observer } from "mobx-react-lite"

export interface TextFieldVerificationProps {
   value: string
   onChange: (value: string) => void
   autoFocus?: boolean
   sx?: SxProps<Theme>
   onComplete?: (value: string) => void
}

const HiddenInput = styled("input")(() => ({
   position: "absolute",
   top: 0,
   right: 0,
   bottom: 0,
   left: 0,
   boxSizing: "border-box",
   color: "transparent",
   background: "transparent",
   caretColor: "transparent",
   outline: "none",
   border: "0 none transparent",
   zIndex: 1,

   "&:-webkit-autofill": {
      opacity: 0,
   },

   "&::-ms-reveal, &::-ms-clear": {
      display: "none",
   }
}))

const Container = styled(Box)(({ theme }) => ({
   position: "relative",
   display: "flex",
   gap: theme.spacing(2),
   height: 60,
}))

interface CharacterBoxProps {
   isSelected: boolean
   isInactive: boolean
   isFilled: boolean
}

const CharacterBox = styled(Box, {
   shouldForwardProp: (prop) => !["isSelected", "isInactive", "isFilled"].includes(prop as string),
})<CharacterBoxProps>(({ theme, isSelected, isInactive, isFilled }) => ({
   height: "100%",
   flexGrow: 1,
   flexBasis: 0,
   textAlign: "center",
   fontSize: 36,
   lineHeight: "50px",
   color: isInactive ? theme.palette.text.disabled : theme.palette.text.primary,
   backgroundColor: isInactive ? theme.palette.action.disabledBackground : theme.palette.background.paper,
   border: `1px solid ${theme.palette.divider}`,
   borderRadius: theme.shape.borderRadius,
   cursor: "text",
   userSelect: "none",
   boxSizing: "border-box",
   display: "flex",
   alignItems: "center",
   justifyContent: "center",
   transition: theme.transitions.create(["border-color", "box-shadow", "color"], {
      duration: theme.transitions.duration.short,
   }),

   ...(isSelected && {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
      color: theme.palette.primary.main,
   }),

   ...(isFilled &&
      !isSelected && {
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light + "20",
   }),

   "&:hover": {
      borderColor: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
   },
}))

export default observer(forwardRef<HTMLInputElement, TextFieldVerificationProps>(function TextFieldVerification(props, ref) {
   const inputRef = useRef<HTMLInputElement | null>(null)
   const textFieldVerificationStore = useStore(sp => new TextFieldVerificationStore(sp), { ...props, inputRef, ref })

   useEffect(() => {
      if (props.autoFocus) {
         inputRef.current?.focus()
      }
   }, [props.autoFocus])

   // Listen for paste events on the whole page
   useEffect(() => {
      const listener = (event: ClipboardEvent) => {
         textFieldVerificationStore.onPaste(event.clipboardData?.getData("text") || '')
      }
      window.addEventListener("paste", listener)

      return () => window.removeEventListener("paste", listener)
   }, [props, textFieldVerificationStore, textFieldVerificationStore.sp])

   return (
      <Container
         data-testid="TextFieldVerification"
         onClick={textFieldVerificationStore.handleClick}
         sx={props.sx}
      >
         <HiddenInput
            aria-label="verification input"
            spellCheck={false}
            value={props.value}
            onChange={textFieldVerificationStore.handleInputChange}
            ref={textFieldVerificationStore.hiddenInputProps.ref}
            onKeyDown={textFieldVerificationStore.handleKeyDown}
            onFocus={textFieldVerificationStore.hiddenInputProps.onFocus}
            onBlur={textFieldVerificationStore.hiddenInputProps.onBlur}
            onSelect={textFieldVerificationStore.hiddenInputProps.onSelect}
         />
         {[...Array(6)].map((_, i) => <CharacterBox
            key={i}
            isSelected={textFieldVerificationStore.state[i].isSelected}
            isInactive={textFieldVerificationStore.state[i].isInactive}
            isFilled={textFieldVerificationStore.state[i].isFilled}
            onClick={textFieldVerificationStore.handleClick}
            id={`TextFieldVerification-input-${i}`}
            data-testid={`TextFieldVerification-input-${i}`}
         >
            {props.value[i] || '.'}
         </CharacterBox>
         )}
      </Container>
   )
}))
